import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Backdrop,
  Slider,
  Button,
  ButtonGroup,
  Switch,
  FormControlLabel,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Collapse,
  Chip,
  Fade,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

/**
 * SettingsPanel Component
 * 
 * A slide-out drawer component for managing Home page customization options.
 * Provides controls for view modes, card customization, display preferences, and more.
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether the settings panel is open
 * @param {function} props.onClose - Callback function to close the panel
 * @param {Object} props.themeStyles - Theme-specific styling object
 * @param {Object} props.preferences - Current user preferences
 * @param {function} props.updatePreferences - Function to update preferences
 * @param {function} props.resetPreferences - Function to reset preferences to defaults
 */
const SettingsPanel = ({
  open,
  onClose,
  themeStyles = {},
  preferences,
  updatePreferences,
  resetPreferences,
}) => {
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [shortcutsExpanded, setShortcutsExpanded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const firstFocusableRef = React.useRef(null);
  
  // Touch gesture support for swipe-to-close
  const [touchStart, setTouchStart] = React.useState(null);
  const [touchEnd, setTouchEnd] = React.useState(null);

  // Minimum swipe distance (in px) to trigger close
  const minSwipeDistance = 50;

  // Simulate save state for visual feedback
  const handlePreferenceUpdate = React.useCallback((updates) => {
    setIsSaving(true);
    updatePreferences(updates);
    setTimeout(() => {
      setIsSaving(false);
    }, 300);
  }, [updatePreferences]);

  const handleResetClick = () => {
    setResetDialogOpen(true);
  };

  const handleResetConfirm = () => {
    resetPreferences();
    setResetDialogOpen(false);
  };

  const handleResetCancel = () => {
    setResetDialogOpen(false);
  };

  const toggleShortcuts = () => {
    setShortcutsExpanded(!shortcutsExpanded);
  };

  // Handle keyboard navigation for shortcuts section
  const handleShortcutsKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleShortcuts();
    }
  };

  // Handle touch gestures for swipe-to-close
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    // Close on right swipe (swipe from left edge to right)
    if (isRightSwipe) {
      onClose();
    }
  };

  // Focus first element when panel opens
  React.useEffect(() => {
    if (open && firstFocusableRef.current) {
      // Small delay to ensure drawer animation completes
      setTimeout(() => {
        firstFocusableRef.current?.focus();
      }, 100);
    }
  }, [open]);

  // Detect if user is on Mac
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const modKey = isMac ? '⌘' : 'Ctrl';

  return (
    <>
      {/* Backdrop for mobile - click to close */}
      <Backdrop
        open={open}
        onClick={onClose}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer - 1,
          display: { xs: 'block', md: 'none' }, // Only show on mobile
        }}
      />

      {/* Settings Drawer */}
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: '400px', md: '450px' },
            maxWidth: '100vw',
            boxSizing: 'border-box',
            ...themeStyles.card,
            transition: preferences.animationsEnabled 
              ? 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s ease'
              : 'none',
            // Mobile: Full screen modal
            height: { xs: '100%', sm: 'auto' },
            maxHeight: { xs: '100vh', sm: '100vh' },
            // Add subtle shadow animation
            ...(preferences.animationsEnabled && open && {
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
            }),
          },
        }}
        transitionDuration={preferences.animationsEnabled ? 300 : 0}
      >
        <Box
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: { xs: 2.5, sm: 2 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid',
              borderColor: themeStyles.card?.borderColor || 'rgba(0, 0, 0, 0.12)',
              // Mobile: Larger touch targets
              minHeight: { xs: '64px', sm: 'auto' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <SettingsIcon
                sx={{
                  fontSize: 24,
                  color: 'primary.main',
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: '18px',
                  ...themeStyles.cardTitle,
                }}
              >
                Settings
              </Typography>
              {/* Saving indicator */}
              <Fade in={isSaving}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                  <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                  <Typography variant="caption" sx={{ color: 'success.main', fontSize: '11px' }}>
                    Saved
                  </Typography>
                </Box>
              </Fade>
            </Box>
            <IconButton
              ref={firstFocusableRef}
              onClick={onClose}
              aria-label="close settings"
              sx={{
                color: themeStyles.cardTitle?.color || 'inherit',
                // Mobile: Larger touch target
                width: { xs: 48, sm: 40 },
                height: { xs: 48, sm: 40 },
                '&:hover': {
                  bgcolor: themeStyles.card?.['&:hover']?.bgcolor || 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <CloseIcon sx={{ fontSize: { xs: 28, sm: 24 } }} />
            </IconButton>
          </Box>

          {/* Content Area - Scrollable */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              // Mobile: More padding for touch-friendly spacing
              p: { xs: 3, sm: 2 },
              pb: { xs: 4, sm: 2 }, // Extra bottom padding on mobile
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: themeStyles.card?.borderColor || 'rgba(0, 0, 0, 0.2)',
                borderRadius: '4px',
                '&:hover': {
                  background: themeStyles.card?.borderColor || 'rgba(0, 0, 0, 0.3)',
                },
              },
            }}
          >
            {/* Card Customization Section */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  fontSize: '14px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  color: themeStyles.cardTitle?.color || 'text.primary',
                }}
              >
                Card Customization
              </Typography>

              {/* Layout Density Presets */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="body2"
                  id="layout-density-label"
                  sx={{
                    mb: 1.5,
                    fontWeight: 500,
                    // Mobile: Larger text
                    fontSize: { xs: '15px', sm: '14px' },
                    color: themeStyles.cardTitle?.color || 'text.primary',
                  }}
                >
                  Layout Density
                </Typography>
                <ButtonGroup
                  fullWidth
                  variant="outlined"
                  aria-labelledby="layout-density-label"
                  sx={{
                    // Mobile: Stack vertically using flexDirection
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    '& .MuiButton-root': {
                      // Mobile: Larger touch targets
                      py: { xs: 1.5, sm: 1 },
                      fontSize: { xs: '15px', sm: '14px' },
                      textTransform: 'none',
                      borderColor: themeStyles.card?.borderColor || 'rgba(0, 0, 0, 0.12)',
                      color: themeStyles.cardTitle?.color || 'text.primary',
                      transition: preferences.animationsEnabled ? 'all 0.2s ease' : 'none',
                      '&.active': {
                        bgcolor: 'primary.main',
                        color: 'white',
                        borderColor: 'primary.main',
                        '&:hover': {
                          bgcolor: 'primary.dark',
                        },
                      },
                      '&:hover': {
                        bgcolor: themeStyles.card?.['&:hover']?.bgcolor || 'rgba(0, 0, 0, 0.04)',
                      },
                    },
                  }}
                >
                  <Button
                    className={preferences.layoutDensity === 'comfortable' ? 'active' : ''}
                    onClick={() => handlePreferenceUpdate({ 
                      layoutDensity: 'comfortable',
                      cardSize: 2.5,
                      cardSpacing: 2
                    })}
                  >
                    Comfortable
                  </Button>
                  <Button
                    className={preferences.layoutDensity === 'cozy' ? 'active' : ''}
                    onClick={() => handlePreferenceUpdate({ 
                      layoutDensity: 'cozy',
                      cardSize: 2,
                      cardSpacing: 1.5
                    })}
                  >
                    Cozy
                  </Button>
                  <Button
                    className={preferences.layoutDensity === 'compact' ? 'active' : ''}
                    onClick={() => handlePreferenceUpdate({ 
                      layoutDensity: 'compact',
                      cardSize: 1.5,
                      cardSpacing: 1
                    })}
                  >
                    Compact
                  </Button>
                </ButtonGroup>
              </Box>

              {/* Card Size Slider */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="body2"
                  id="card-size-label"
                  sx={{
                    mb: 1.5,
                    fontWeight: 500,
                    // Mobile: Larger text
                    fontSize: { xs: '15px', sm: '14px' },
                    color: themeStyles.cardTitle?.color || 'text.primary',
                  }}
                >
                  Card Size: {preferences.cardSize.toFixed(1)}
                </Typography>
                <Slider
                  value={preferences.cardSize}
                  onChange={(e, value) => handlePreferenceUpdate({ cardSize: value })}
                  min={1}
                  max={3}
                  step={0.1}
                  marks={[
                    { value: 1, label: 'Small' },
                    { value: 2, label: 'Medium' },
                    { value: 3, label: 'Large' },
                  ]}
                  valueLabelDisplay="auto"
                  aria-labelledby="card-size-label"
                  aria-valuetext={`Card size ${preferences.cardSize.toFixed(1)}`}
                  sx={{
                    color: 'primary.main',
                    transition: preferences.animationsEnabled ? 'all 0.2s ease' : 'none',
                    // Mobile: Larger touch target for slider thumb
                    '& .MuiSlider-thumb': {
                      width: { xs: 24, sm: 20 },
                      height: { xs: 24, sm: 20 },
                      transition: preferences.animationsEnabled ? 'all 0.2s ease' : 'none',
                    },
                    '& .MuiSlider-track': {
                      transition: preferences.animationsEnabled ? 'all 0.2s ease' : 'none',
                    },
                    '& .MuiSlider-markLabel': {
                      color: themeStyles.cardDescription?.color || 'text.secondary',
                      fontSize: { xs: '12px', sm: '11px' },
                    },
                  }}
                />
              </Box>

              {/* Card Spacing Slider */}
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  id="card-spacing-label"
                  sx={{
                    mb: 1.5,
                    fontWeight: 500,
                    // Mobile: Larger text
                    fontSize: { xs: '15px', sm: '14px' },
                    color: themeStyles.cardTitle?.color || 'text.primary',
                  }}
                >
                  Card Spacing: {preferences.cardSpacing.toFixed(1)}
                </Typography>
                <Slider
                  value={preferences.cardSpacing}
                  onChange={(e, value) => handlePreferenceUpdate({ cardSpacing: value })}
                  min={0.5}
                  max={3}
                  step={0.1}
                  marks={[
                    { value: 0.5, label: 'Tight' },
                    { value: 1.75, label: 'Normal' },
                    { value: 3, label: 'Loose' },
                  ]}
                  valueLabelDisplay="auto"
                  aria-labelledby="card-spacing-label"
                  aria-valuetext={`Card spacing ${preferences.cardSpacing.toFixed(1)}`}
                  sx={{
                    color: 'primary.main',
                    transition: preferences.animationsEnabled ? 'all 0.2s ease' : 'none',
                    // Mobile: Larger touch target for slider thumb
                    '& .MuiSlider-thumb': {
                      width: { xs: 24, sm: 20 },
                      height: { xs: 24, sm: 20 },
                      transition: preferences.animationsEnabled ? 'all 0.2s ease' : 'none',
                    },
                    '& .MuiSlider-track': {
                      transition: preferences.animationsEnabled ? 'all 0.2s ease' : 'none',
                    },
                    '& .MuiSlider-markLabel': {
                      color: themeStyles.cardDescription?.color || 'text.secondary',
                      fontSize: { xs: '12px', sm: '11px' },
                    },
                  }}
                />
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Display Preferences Section */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  fontSize: '14px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  color: themeStyles.cardTitle?.color || 'text.primary',
                }}
              >
                Display Preferences
              </Typography>

              {/* Show Descriptions Toggle */}
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.showDescriptions}
                      onChange={(e) => handlePreferenceUpdate({ showDescriptions: e.target.checked })}
                      color="primary"
                      sx={{
                        transition: preferences.animationsEnabled ? 'all 0.2s ease' : 'none',
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: themeStyles.cardTitle?.color || 'text.primary',
                          transition: preferences.animationsEnabled ? 'color 0.2s ease' : 'none',
                        }}
                      >
                        Show Card Descriptions
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: themeStyles.cardDescription?.color || 'text.secondary',
                        }}
                      >
                        Display description text on navigation cards
                      </Typography>
                    </Box>
                  }
                  sx={{ width: '100%', m: 0 }}
                />
              </Box>

              {/* Animations Enabled Toggle */}
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.animationsEnabled}
                      onChange={(e) => handlePreferenceUpdate({ animationsEnabled: e.target.checked })}
                      color="primary"
                      sx={{
                        transition: preferences.animationsEnabled ? 'all 0.2s ease' : 'none',
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: themeStyles.cardTitle?.color || 'text.primary',
                          transition: preferences.animationsEnabled ? 'color 0.2s ease' : 'none',
                        }}
                      >
                        Enable Animations
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: themeStyles.cardDescription?.color || 'text.secondary',
                        }}
                      >
                        Show smooth transitions and effects
                      </Typography>
                    </Box>
                  }
                  sx={{ width: '100%', m: 0 }}
                />
              </Box>

              {/* Icon Size Selector */}
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  id="icon-size-label"
                  sx={{
                    mb: 1.5,
                    fontWeight: 500,
                    // Mobile: Larger text
                    fontSize: { xs: '15px', sm: '14px' },
                    color: themeStyles.cardTitle?.color || 'text.primary',
                  }}
                >
                  Icon Size
                </Typography>
                <ToggleButtonGroup
                  value={preferences.iconSize}
                  exclusive
                  onChange={(e, value) => {
                    if (value !== null) {
                      handlePreferenceUpdate({ iconSize: value });
                    }
                  }}
                  fullWidth
                  aria-labelledby="icon-size-label"
                  sx={{
                    // Mobile: Stack vertically using flexDirection
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    '& .MuiToggleButton-root': {
                      // Mobile: Larger touch targets
                      py: { xs: 1.5, sm: 1 },
                      fontSize: { xs: '15px', sm: '14px' },
                      textTransform: 'none',
                      borderColor: themeStyles.card?.borderColor || 'rgba(0, 0, 0, 0.12)',
                      color: themeStyles.cardTitle?.color || 'text.primary',
                      transition: preferences.animationsEnabled ? 'all 0.2s ease' : 'none',
                      '&.Mui-selected': {
                        bgcolor: 'primary.main',
                        color: 'white',
                        borderColor: 'primary.main',
                        '&:hover': {
                          bgcolor: 'primary.dark',
                        },
                      },
                      '&:hover': {
                        bgcolor: themeStyles.card?.['&:hover']?.bgcolor || 'rgba(0, 0, 0, 0.04)',
                      },
                    },
                  }}
                >
                  <ToggleButton value="small">Small</ToggleButton>
                  <ToggleButton value="medium">Medium</ToggleButton>
                  <ToggleButton value="large">Large</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Keyboard Shortcuts Section */}
            <Box sx={{ mb: 3 }}>
              <Box
                onClick={toggleShortcuts}
                onKeyDown={handleShortcutsKeyDown}
                tabIndex={0}
                role="button"
                aria-expanded={shortcutsExpanded}
                aria-label="Toggle keyboard shortcuts"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  mb: shortcutsExpanded ? 2 : 0,
                  borderRadius: 1,
                  p: 0.5,
                  '&:hover': {
                    opacity: 0.8,
                  },
                  '&:focus': {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: '2px',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <KeyboardIcon
                    sx={{
                      fontSize: 18,
                      color: 'primary.main',
                    }}
                  />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      fontSize: '14px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      color: themeStyles.cardTitle?.color || 'text.primary',
                    }}
                  >
                    Keyboard Shortcuts
                  </Typography>
                </Box>
                <IconButton size="small">
                  {shortcutsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>

              <Collapse 
                in={shortcutsExpanded}
                timeout={preferences.animationsEnabled ? 300 : 0}
              >
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: 1.5,
                  ...(preferences.animationsEnabled && {
                    animation: shortcutsExpanded ? 'fadeIn 0.3s ease' : 'none',
                    '@keyframes fadeIn': {
                      from: { opacity: 0 },
                      to: { opacity: 1 },
                    },
                  }),
                }}>
                  {/* Search Shortcut */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 1.5,
                      borderRadius: 1,
                      ...themeStyles.card,
                      border: '1px solid',
                      borderColor: themeStyles.card?.borderColor || 'rgba(0, 0, 0, 0.12)',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: themeStyles.cardTitle?.color || 'text.primary',
                      }}
                    >
                      Focus Search
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Chip
                        label={modKey}
                        size="small"
                        sx={{
                          fontFamily: 'monospace',
                          fontWeight: 600,
                          fontSize: '11px',
                        }}
                      />
                      <Chip
                        label="K"
                        size="small"
                        sx={{
                          fontFamily: 'monospace',
                          fontWeight: 600,
                          fontSize: '11px',
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Settings Shortcut */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 1.5,
                      borderRadius: 1,
                      ...themeStyles.card,
                      border: '1px solid',
                      borderColor: themeStyles.card?.borderColor || 'rgba(0, 0, 0, 0.12)',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: themeStyles.cardTitle?.color || 'text.primary',
                      }}
                    >
                      Open Settings
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Chip
                        label={modKey}
                        size="small"
                        sx={{
                          fontFamily: 'monospace',
                          fontWeight: 600,
                          fontSize: '11px',
                        }}
                      />
                      <Chip
                        label=","
                        size="small"
                        sx={{
                          fontFamily: 'monospace',
                          fontWeight: 600,
                          fontSize: '11px',
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Escape Shortcut */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 1.5,
                      borderRadius: 1,
                      ...themeStyles.card,
                      border: '1px solid',
                      borderColor: themeStyles.card?.borderColor || 'rgba(0, 0, 0, 0.12)',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: themeStyles.cardTitle?.color || 'text.primary',
                      }}
                    >
                      Close Panels
                    </Typography>
                    <Chip
                      label="Esc"
                      size="small"
                      sx={{
                        fontFamily: 'monospace',
                        fontWeight: 600,
                        fontSize: '11px',
                      }}
                    />
                  </Box>
                </Box>
              </Collapse>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Reset to Defaults Section */}
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  fontSize: { xs: '15px', sm: '14px' },
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  color: themeStyles.cardTitle?.color || 'text.primary',
                }}
              >
                Reset Settings
              </Typography>
              <Button
                variant="outlined"
                color="error"
                fullWidth
                startIcon={<RestartAltIcon sx={{ fontSize: { xs: 22, sm: 20 } }} />}
                onClick={handleResetClick}
                sx={{
                  // Mobile: Larger touch target
                  py: { xs: 2, sm: 1.5 },
                  fontSize: { xs: '16px', sm: '14px' },
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Reset to Defaults
              </Button>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 1,
                  fontSize: { xs: '13px', sm: '12px' },
                  color: themeStyles.cardDescription?.color || 'text.secondary',
                  textAlign: 'center',
                }}
              >
                This will restore all settings to their default values
              </Typography>
            </Box>
          </Box>
        </Box>
      </Drawer>

      {/* Reset Confirmation Dialog */}
      <Dialog
        open={resetDialogOpen}
        onClose={handleResetCancel}
        PaperProps={{
          sx: {
            ...themeStyles.card,
            minWidth: { xs: '90%', sm: '400px' },
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            color: themeStyles.cardTitle?.color || 'text.primary',
          }}
        >
          Reset Settings to Defaults?
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              color: themeStyles.cardDescription?.color || 'text.secondary',
            }}
          >
            This will reset all your customization settings including view mode, card size, 
            spacing, and display preferences to their default values. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={handleResetCancel}
            sx={{
              textTransform: 'none',
              color: themeStyles.cardTitle?.color || 'text.primary',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleResetConfirm}
            variant="contained"
            color="error"
            sx={{
              textTransform: 'none',
            }}
          >
            Reset
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SettingsPanel;
